import type { FocusEventHandler, FunctionComponent, RefObject } from 'react'
import React, { useEffect, useRef, useState } from 'react'

import { useFocusRing } from '@react-aria/focus'
import { useSliderThumb } from '@react-aria/slider'
import type { SliderState } from '@react-stately/slider'
import classNames from 'classnames'

import { TypographyV2 } from '../../Seatbelt2/Foundations/Typography'
import type { LabelFormatter } from '../Slider'
import styles from './SliderThumb.module.scss'

/*
 * This component should not be used outside of Slider
 * See Slider.stories.tsx for example usage
 */
export const SliderThumb: FunctionComponent<SliderThumbProps> = ({
  index,
  state,
  trackRef,
  labelFormatter,
  'aria-labelledby': ariaLabelledBy,
  onFocus,
  ...rest
}) => {
  const inputRef = useRef<HTMLInputElement>(null)
  const { thumbProps, inputProps, isDragging } = useSliderThumb(
    {
      index,
      trackRef,
      inputRef,
      ...rest,
    },
    state
  )

  const { focusProps, isFocusVisible } = useFocusRing()

  const handleFocus: FocusEventHandler<HTMLInputElement> = (e) => {
    focusProps.onFocus && focusProps.onFocus(e)
    onFocus && onFocus(e)
  }

  const isModified = useIsModified(state.getThumbValue(index))

  // Adjusts z-index so left thumb can be dragged when both thumbs are at 100%.
  // See https://github.com/adobe/react-spectrum/issues/3387
  const zIndex = index === 0 && state.getThumbPercent(1) === 1 ? 2 : undefined

  return (
    <div className={styles.thumbWrapper}>
      <TypographyV2
        {...thumbProps}
        aria-hidden="true"
        className={styles.thumbValue}
        textStyle="sb-text-body-small-regular"
        data-testid={`slider-thumb-label-${index}`}
      >
        {labelFormatter({ value: state.getThumbValue(index), type: 'current' })}
      </TypographyV2>
      <div
        {...thumbProps}
        style={{ ...thumbProps.style, zIndex }}
        className={classNames(
          styles.thumb,
          isFocusVisible && styles.thumbFocused,
          isDragging && styles.thumbDragging,
          isModified && styles.thumbModified
        )}
        data-testid={`slider-thumb-${index}`}
      >
        <input
          ref={inputRef}
          {...{
            ...inputProps,
            ...focusProps,
            ...(ariaLabelledBy ? { 'aria-labelledby': ariaLabelledBy } : {}),
            onFocus: handleFocus,
          }}
          className={styles.input}
        />
      </div>
    </div>
  )
}

///////// IMPLEMENTATION /////////

export type SliderThumbProps = {
  /*
   * State generated by react-aria useSliderState
   */
  state: SliderState
  /*
   * Ref for slider track
   */
  trackRef: RefObject<Element>
  /*
   * Index of thumb (will be 0 for a single-thumb slider, 0 and 1 for a dual-thumb slider)
   */
  index: number
  /*
   * Callback for formatting labels
   */
  labelFormatter: LabelFormatter
  /**
   * Optional aria-labelledby to be applied to input. Ensure ID used is unique.
   * If an aria-label prop was provided to Slider this will be applied automatically via state.
   * If no aria-label prop was provided, aria-labelledby *must* be provided for accessibility.
   */
  'aria-labelledby'?: string
  onFocus?: FocusEventHandler<HTMLInputElement>
}

const useIsModified = (currentValue: number) => {
  const [initialValue] = useState<number>(currentValue)
  const [isModified, setIsModified] = useState<boolean>(false)

  const valueChanged = initialValue !== currentValue

  useEffect(() => {
    if (!isModified && valueChanged) {
      setIsModified(true)
    }
  }, [isModified, valueChanged])

  return isModified
}
